import React from 'react';
import { useSelector } from 'react-redux';
import { tierHasFeature } from '../../../lib/featureLevels';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { classNames } from '../../../utilities/classNames';
import TimeSeriesChart from './TimeSeriesChart';
import ActionBreakdownTable from './ActionBreakdownTable';
import PeakAnalysisCard from './PeakAnalysisCard';
import FunnelView from './FunnelView';
import RegionalBenchmarkCard from './RegionalBenchmarkCard';

const periods = [
  { value: '7d', label: '7d' },
  { value: '30d', label: '30d' },
  { value: '90d', label: '90d' },
  { value: '1y', label: '1y' },
];

const viewsLines = [
  { key: 'load', name: 'Views', color: '#3b82f6' },
  { key: 'search', name: 'Searches', color: '#6366f1' },
  { key: 'nearby', name: 'Nearby', color: '#8b5cf6' },
  { key: 'discover', name: 'Discover', color: '#7c3aed' },
];

const engagementLines = [
  { key: 'checkin', name: 'Check-Ins', color: '#10b981' },
  { key: 'sip', name: 'Sips', color: '#f43f5e' },
  { key: 'uniqueVisitors', name: 'Unique Visitors', color: '#f59e0b' },
];

const conversionLines = [
  { key: 'call', name: 'Phone Calls', color: '#f97316' },
  { key: 'website', name: 'Website', color: '#06b6d4' },
  { key: 'directions', name: 'Directions', color: '#14b8a6' },
  { key: 'reservation', name: 'Reservations', color: '#84cc16' },
];

export default function AdvancedAnalyticsView({ data, period, onPeriodChange }) {
  const user = useSelector((s) => s.auth.user);
  // Was `featureLevel < 111`, a magnitude test against a bitmask, and the API
  // sends subscriptionTier now rather than the mask.
  const isAdminViewingLowTier = user?.role === 'admin'
    && !tierHasFeature(data?.listing?.subscriptionTier, 'ADVANCED_ANALYTICS');

  // Flatten timeSeries for charts: merge actions + derived into top-level keys
  const flatTimeSeries = (data?.timeSeries || []).map((d) => ({
    date: d.date,
    ...(d.actions || {}),
    ...(d.derived || {}),
  }));

  return (
    <div className="space-y-6">
      {/* Period selector */}
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-gray-700">Period:</span>
        <div className="flex bg-gray-100 rounded-lg p-0.5">
          {periods.map((p) => (
            <button
              key={p.value}
              onClick={() => onPeriodChange(p.value)}
              className={classNames(
                'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
                period === p.value
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              )}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Admin info banner */}
      {isAdminViewingLowTier && (
        <div className="rounded-md bg-yellow-50 p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 flex-shrink-0" />
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                You are viewing advanced analytics as an admin. This listing does not meet the tier
                requirement for self-service access.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Time Series Charts */}
      <TimeSeriesChart
        title="Views & Discovery"
        description="How users find and view your listing"
        data={flatTimeSeries}
        lines={viewsLines}
      />

      <TimeSeriesChart
        title="Engagement"
        description="Check-ins, sips, and unique visitors over time"
        data={flatTimeSeries}
        lines={engagementLines}
      />

      <TimeSeriesChart
        title="Conversions"
        description="Actions that drive real-world visits"
        data={flatTimeSeries}
        lines={conversionLines}
      />

      {/* Breakdown + Peaks */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ActionBreakdownTable
          metrics={data?.aggregatedMetrics?.actions}
          timeSeries={flatTimeSeries}
        />
        <PeakAnalysisCard peaks={data?.aggregatedMetrics?.peaks} />
      </div>

      {/* Funnel */}
      <FunnelView metrics={data?.aggregatedMetrics?.actions} />

      {/* Regional Benchmark */}
      {data?.regionalBenchmark && (
        <RegionalBenchmarkCard benchmark={data.regionalBenchmark} />
      )}
    </div>
  );
}
